import React, { useState, useEffect, useRef, useCallback } from "react";
import { Share2, ThumbsUp } from "lucide-react";
import { RWebShare } from "react-web-share";
import { connect, useSelector } from "react-redux";
import _ from "lodash";
import { Autocomplete, TextField } from "@mui/material";
import { getUniqueListBy } from "@components/Common/Function";
import ModalPopup from "@components/Common/ModalPopup";
import { addFeedbackApi } from "@services/MarketWorking/MyDiary/addFeedbackApi";
import { addDealerNameApi } from "@services/MarketWorking/MyTeam/addDealerNameApi";
import { getDealerNameApiForHOUser } from "@services/hoUser/getDealerNameApiForHOUser";
import { addWSSNameApiForHOUser } from "@services/hoUser/addWSSNameApiForHOUser";
import { getZSMTerritoryApi } from "@services/zsm/getZSMTerritoryApi";
import { getZSMDealerNameApi } from "@services/zsm/getZSMDealerNameApi";
import { getZSMWssNameApi } from "@services/zsm/getZSMWssNameApi";
import { getRmdmWssApi } from "@services/rmdm/getRmdmWssApi";
import { getDealerNameApi } from "@services/rmdm/getDealerNameApi";
import { Input, Textarea, Button, Tabs, FileUpload, Loader } from "../../shared/components";
import { useNewFeedbackForm, useValidation } from "../hooks";
import PreviousObservation from "./PreviousObservation";

const territoryAutoCompletePlaceHolder = {
  attributes: {},
  Id: "",
  SH2_Name__c: "",
  SH2_Code__c: "",
  Name: "",
  SH3_Code__c: "",
};

const wssOrDelaerPlaceHoder = {
  Dealer_Name__c: "",
  DealerCode__c: "",
  CustomerName__c: "",
  Customer_Code__c: "",
  Product_Group_3_Description__c: "",
};

const rmdmWssPlaceholder = {
  CustomerName__c: "",
  Customer_Code__c: "",
};

/**
 * NewFeedback Component
 * 
 * Complex form for adding observations/feedback with 4 tabs:
 * - Dealer: Dealer-specific observations
 * - WSS: Wholesaler observations  
 * - Product: Product feedback
 * - Team: Team-related observations
 * 
 * Features:
 * - Dynamic field display based on user role (SH3, HO, ZSM, RMDM, etc.)
 * - Territory/Cluster selection
 * - File upload with compression
 * - Form validation
 * - Share functionality
 * 
 * Note: This is a refactored version maintaining all original functionality
 * with improved code organization and Tailwind CSS styling.
 */
const NewFeedback = ({
  addFeedbackApi,
  getZSMTerritoryApi,
  getZSMDealerNameApi,
  getZSMWssNameApi,
  getDealerNameApiForHOUser,
  getRmdmWssApi,
  getDealerNameApi,
}) => {
  // Redux selectors
  const territoryOptions = useSelector((state) => state?.territory?.territoryDetails);
  const clusterCodeOption = useSelector((state) => state?.clusterCode?.clusterCodeList);
  const products = useSelector((state) => state?.product?.productList);
  const BMWss = useSelector((state) => state?.bmWss?.wssList);
  const userType = useSelector((state) =>
    state?.userType?.type !== "Other"
      ? state?.userType?.type
      : state?.userDetails?.details?.Title
  );

  const hoDivisionGroup = territoryOptions?.[0]?.Division_Code__c;
  const hoSalesGroup = territoryOptions?.[0]?.Sales_Group__c;

  // Custom hook for form management
  const {
    tab,
    feedback,
    file,
    formValidation,
    isSubmit,
    cluster,
    territory,
    wssDealerOrProduct,
    rmdmWssValue,
    setFeedback,
    setFile,
    setFormValidation,
    setIsSubmit,
    setCluster,
    setTerritory,
    setWssDealerOrProduct,
    setRmdmWssValue,
    handleTabChange: customHandleTabChange,
    onChange,
    fileHandler,
  } = useNewFeedbackForm("Dealer", userType);

  // Additional state for API data
  const [dealerOption, setDealerOption] = useState([]);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [sh2Code, setSh2Code] = useState("");
  const [loading, setLoading] = useState(false);
  const [recordType, setRecordType] = useState("Dealer");
  const [wss, setWSS] = useState([]);
  const [sh3CodeForTerritory, setSH3CodeForTerritory] = useState("");
  const formRef = useRef();
  const [territoryPickList, setTerritoryPickList] = useState([]);
  const [dealerLoader, setDealerLoader] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const tabs = ["Dealer", "WSS", "Product", "Team"];

  // Custom validation hook
  const validateForm = useValidation(tab, userType, sh3CodeForTerritory, formValidation);

  // Extended onChange handler for complex logic
  const onChangeExtended = useCallback((e, newValue, name) => {
    if (e.target.name === "clusterCode") {
      setSH3CodeForTerritory(e?.target?.value);
      setTerritory(null);
      setWssDealerOrProduct(null);
      setDealerOption([]);
      setTerritoryPickList([]);
    }
    if (name === "dealerId") {
      setFeedback((prev) => ({
        ...prev,
        dealerId: newValue?.Id || "",
      }));
    } else if (name === "rmdmWss") {
      setFeedback((prev) => ({
        ...prev,
        rmdmWss: newValue || "",
      }));
    } else if (name === "territory") {
      setWssDealerOrProduct(null);
      setDealerOption([]);
      setFeedback((prev) => ({
        ...prev,
        territory: newValue ? `${newValue.SH2_Name__c}:${newValue.SH2_Code__c}` : "",
      }));
      setRecordType("Dealer");
      setSh2Code(newValue?.SH2_Code__c || "");
    } else {
      onChange(e, newValue, name);
    }
  }, [onChange, setFeedback, setTerritory, setWssDealerOrProduct]);

  // File upload handler with compression (using custom hook)
  const FileHandler = useCallback(async (e) => {
    setIsUploading(true);
    await fileHandler(e);
    setIsUploading(false);
  }, [fileHandler]);

  const onSubmit = useCallback(
    (e) => {
      e.preventDefault();
      setIsSubmit(true);
      setFormValidation(validateForm(feedback));
    },
    [feedback, validateForm, setFormValidation, setIsSubmit]
  );

  // Get dealer name for SH3 users
  useEffect(() => {
    if (!_.isEmpty(recordType) && recordType !== "Team" && userType === "SH3") {
      (async () => {
        let response;
        if (
          recordType === "Dealer" &&
          recordType !== "Product" &&
          !_.isEmpty(feedback?.territory)
        ) {
          setDealerLoader(true);
          response = await addDealerNameApi(tab, sh2Code);
        } else if (recordType === "WSS") {
          setDealerLoader(true);
          response = await addDealerNameApi(tab);
        }
        if (response?.success) {
          setDealerOption(response?.data);
          setDealerLoader(false);
          if (response?.data?.Name) {
            const FilterWss = getUniqueListBy(response?.data?.Name, "CustomerName__c");
            setWSS(FilterWss);
            setDealerLoader(false);
          }
          setRecordType("");
        }
      })();
    }
  }, [recordType, feedback?.territory, tab, sh2Code, userType]);

  // Get dealer name for HO users
  useEffect(() => {
    if (
      !_.isEmpty(recordType) &&
      recordType !== "Team" &&
      (userType === "HO Collaborator" || userType === "HO Auditor")
    ) {
      (async () => {
        let response;
        if (recordType === "Dealer" && !_.isEmpty(feedback?.territory)) {
          setDealerLoader(true);
          response = await getDealerNameApiForHOUser(hoDivisionGroup, hoSalesGroup, sh2Code);
        }
        if (recordType === "WSS" && !_.isEmpty(feedback?.clusterCode)) {
          setDealerLoader(true);
          response = await addWSSNameApiForHOUser(
            hoDivisionGroup,
            hoSalesGroup,
            feedback?.clusterCode
          );
        }
        if (response?.success) {
          setDealerLoader(false);
          setDealerOption(response?.data);
          if (recordType === "WSS") {
            const FilterWss = getUniqueListBy(response?.data, "CustomerName__c");
            setDealerLoader(false);
            setWSS(FilterWss);
          }
        }
      })();
    }
  }, [recordType, feedback?.clusterCode, feedback?.territory, userType, hoDivisionGroup, hoSalesGroup, sh2Code]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get dealer name for ZSM users
  useEffect(() => {
    if (
      !_.isEmpty(recordType) &&
      recordType !== "Team" &&
      (userType === "SH6" || userType === "SH5" || userType === "SH7")
    ) {
      (async () => {
        let response;
        if (recordType === "Dealer" && !_.isEmpty(feedback?.territory)) {
          setDealerLoader(true);
          response = await getZSMDealerNameApi(
            sh3CodeForTerritory,
            feedback?.territory?.split(":")?.[1]
          );
        }
        if (recordType === "WSS" && !_.isEmpty(feedback?.clusterCode)) {
          setDealerLoader(true);
          response = await getZSMWssNameApi(feedback?.clusterCode);
        }
        if (response?.success) {
          setDealerLoader(false);
          setDealerOption(response?.data);
          if (recordType === "WSS") {
            const FilterWss = getUniqueListBy(response?.data, "CustomerName__c");
            setDealerLoader(false);
            setWSS(FilterWss);
          }
        }
      })();
    }
  }, [recordType, feedback?.clusterCode, feedback?.territory, userType, sh3CodeForTerritory]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get WSS for RMDM/RBDM users
  useEffect(() => {
    if (!_.isEmpty(feedback?.clusterCode) && recordType !== "WSS") {
      (async () => {
        const response = await getRmdmWssApi(feedback?.clusterCode);
        if (response?.success) {
          setWSS(response?.data);
        }
      })();
    }
  }, [feedback?.clusterCode, recordType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get dealer name for RMDM/RBDM users
  useEffect(() => {
    if (
      !_.isEmpty(recordType) &&
      recordType !== "Team" &&
      (userType === "MH3" || userType === "RBDM Collaborator")
    ) {
      (async () => {
        let response;
        if (recordType === "Dealer" && !_.isEmpty(feedback?.rmdmWss)) {
          setDealerLoader(true);
          response = await getDealerNameApi([feedback?.rmdmWss]);
        }
        if (recordType === "WSS" && !_.isEmpty(feedback?.clusterCode)) {
          setDealerLoader(true);
          response = await getRmdmWssApi(feedback?.clusterCode);
        }
        if (response?.success) {
          setDealerLoader(false);
          setDealerOption(response?.data?.records);
          if (recordType === "WSS") {
            const FilterWss = getUniqueListBy(response?.data, "Customer_Code__c");
            setDealerLoader(false);
            setWSS(FilterWss);
          }
        }
      })();
    }
  }, [recordType, feedback?.rmdmWss, feedback?.clusterCode, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Get territory list for ZSM/HO users
  useEffect(() => {
    if (
      !_.isEmpty(sh3CodeForTerritory) &&
      (recordType === "Dealer" || recordType === "Team") &&
      userType !== "SH3" &&
      userType !== "MH3" &&
      userType !== "RBDM Collaborator"
    ) {
      (async () => {
        const response = await getZSMTerritoryApi(sh3CodeForTerritory);
        if (response?.success) {
          setTerritoryPickList(response?.data?.records);
        } else {
          setTerritoryPickList([]);
        }
      })();
    }
  }, [sh3CodeForTerritory, recordType, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Submit form
  useEffect(() => {
    if (Object?.keys(formValidation)?.length === 0 && isSubmit) {
      (async () => {
        const dealerIDValue = tab === "Team" ? feedback?.territory : feedback?.dealerId;
        const data = {
          dealerId: dealerIDValue,
          Title: feedback?.Title,
          Observation: feedback?.Observation,
          recordType: `My ${tab}`,
          fileExtension: file?.fileExtension,
          fileName: file?.fileName,
          base64: file?.base64,
        };
        setLoading(true);
        const response = await addFeedbackApi(data);
        setLoading(false);
        if (response?.success) {
          setIsSubmitModalOpen(true);
          setRmdmWssValue(rmdmWssPlaceholder);
          setIsSubmit(false);
        }
        // Reset form
        setTerritory(territoryAutoCompletePlaceHolder);
        setCluster(null);
        setWssDealerOrProduct(wssOrDelaerPlaceHoder);
        setFeedback({
          territory: "",
          dealerId: "",
          Title: "",
          Observation: "",
          clusterCode: "",
          rmdmWss: "",
        });
        formRef.current.value = "";
        setFile({
          base64: "",
          fileExtension: "",
          fileName: "",
        });
        setSH3CodeForTerritory("");
      })();
    }
    if (Object?.keys(formValidation)?.length !== 0) {
      setIsSubmit(false);
    }
  }, [isSubmit, formValidation, feedback, tab, file, addFeedbackApi]); // eslint-disable-line react-hooks/exhaustive-deps

  const getNameList = () => {
    if (tab === "Dealer" && userType === "SH3" && dealerOption?.Name?.length)
      return dealerOption?.Name;
    if (
      tab === "Dealer" &&
      (userType === "HO Collaborator" ||
        userType === "SH6" ||
        userType === "SH5" ||
        userType === "SH7" ||
        userType === "HO Auditor") &&
      dealerOption?.length
    )
      return dealerOption;
    if (tab === "Dealer" && (userType === "MH3" || userType === "RBDM Collaborator"))
      return dealerOption;
    if (tab === "WSS") {
      if (userType === "SH3") {
        return BMWss;
      } else return wss;
    }
    if (tab === "Product") return products;
  };

  // Extended tab change handler that also updates record type and resets dependent state
  const handleTabChangeExtended = useCallback((newTab) => {
    customHandleTabChange(newTab);
    setRecordType(newTab);
    setDealerOption([]);
    setSH3CodeForTerritory("");
    setTerritoryPickList([]);
  }, [customHandleTabChange]);

  return (
    <>
      <Loader loading={isUploading} />
      {isSubmit && Object?.keys(formValidation)?.length === 0 && <Loader loading={loading} />}

      <div className="px-4 py-2.5 w-full">
        <h6 className="py-2.5 px-0 text-[#3901ff] font-semibold text-base leading-5 my-0">New Observations</h6>
        <div className="bg-white shadow-[0.075rem_0.075rem_0.334rem_rgba(20,20,20,0.07)] rounded-[0.3rem] w-full">
          {/* Tabs */}
          <Tabs tabs={tabs} activeTab={tab} onChange={handleTabChangeExtended} />

          {/* Tab Content */}
          <div className="w-auto p-2.5 rounded-lg">
            <form ref={formRef}>
              {/* Cluster Code Selection - for ZSM/HO/RMDM users */}
              {(tab === "Dealer" || tab === "Team" || tab === "WSS") &&
                (userType === "SH6" ||
                  userType === "SH5" ||
                  userType === "SH7" ||
                  userType === "HO Collaborator" ||
                  userType === "HO Auditor" ||
                  userType === "MH3" ||
                  userType === "RBDM Collaborator") && (
                  <>
                    <Autocomplete
                      value={cluster}
                      id="cluster-select"
                      className="myDealer__categorySelect"
                      options={
                        !clusterCodeOption || clusterCodeOption?.length === 0
                          ? [
                              {
                                SH3_Name__c: "Loading...",
                                SH3_Code__c: "",
                                Cluster_Description__c: "Loading...",
                                CLUSTER_CODE__c: "",
                              },
                            ]
                          : getUniqueListBy(
                              clusterCodeOption,
                              userType === "MH3" || userType === "RBDM Collaborator"
                                ? "CLUSTER_CODE__c"
                                : "SH3_Name__c"
                            )
                      }
                      getOptionLabel={(option) =>
                        userType !== "MH3" && userType !== "RBDM Collaborator"
                          ? `${option?.SH3_Name__c}${
                              option?.SH3_Code__c && `:${option?.SH3_Code__c}`
                            }`
                          : `${option?.Cluster_Description__c}${
                              option?.CLUSTER_CODE__c && `:${option?.CLUSTER_CODE__c}`
                            }`
                      }
                      onChange={(e, newValue) => {
                        setCluster(newValue);
                        onChangeExtended({
                          target: {
                            name: "clusterCode",
                            value:
                              userType === "MH3" || userType === "RBDM Collaborator"
                                ? newValue?.CLUSTER_CODE__c
                                : newValue?.SH3_Code__c,
                          },
                        });
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="Select Cluster" />}
                    />
                    {!sh3CodeForTerritory && formValidation?.clusterCode && (
                      <p className="m-0 text-[#EF5464] text-[0.60rem] py-[5px] whitespace-nowrap">{formValidation?.clusterCode}</p>
                    )}
                  </>
                )}

              {/* Territory Selection - for ZSM/HO users (Dealer/Team tabs) */}
              {(tab === "Dealer" || tab === "Team") &&
                (userType === "SH6" ||
                  userType === "SH5" ||
                  userType === "SH7" ||
                  userType === "HO Collaborator" ||
                  userType === "HO Auditor") && (
                  <Autocomplete
                    value={territory}
                    id="territory-select"
                    className="myDealer__categorySelect"
                    options={
                      !territoryPickList || territoryPickList?.length === 0
                        ? [{ SH2_Name__c: "Loading...", SH2_Code__c: "" }]
                        : getUniqueListBy(territoryPickList, "SH2_Code__c")
                    }
                    getOptionLabel={(option) =>
                      `${option?.SH2_Name__c}${
                        option?.SH2_Code__c ? `:${option?.SH2_Code__c}` : ""
                      }`
                    }
                    onChange={(e, newValue) => {
                      setTerritory(newValue);
                      onChangeExtended(e, newValue, "territory");
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Select Territory" />}
                  />
                )}

              {/* WSS Selection - for RMDM users (Dealer tab) */}
              {(tab === "Dealer" || tab === "Team") &&
                (userType === "MH3" || userType === "RBDM Collaborator") && (
                  <>
                    <Autocomplete
                      value={rmdmWssValue}
                      id="rmdm-wss-select"
                      className="myDealer__categorySelect"
                      options={
                        !wss || wss?.length === 0
                          ? [{ CustomerName__c: "Loading...", Customer_Code__c: "" }]
                          : getUniqueListBy(wss, "Customer_Code__c")
                      }
                      getOptionLabel={(option) =>
                        `${option?.CustomerName__c}${
                          option?.Customer_Code__c ? `:${option?.Customer_Code__c}` : ""
                        }`
                      }
                      onChange={(e, newValue) => {
                        setRmdmWssValue(newValue);
                        onChange(e, newValue?.WSS_Territory_Code__c, "rmdmWss");
                      }}
                      renderInput={(params) => <TextField {...params} placeholder="Select WSS" />}
                    />
                    {!feedback.rmdmWss && formValidation?.rmdmWss && (
                      <p className="m-0 text-[#EF5464] text-[0.60rem] py-[5px] whitespace-nowrap">{formValidation?.rmdmWss}</p>
                    )}
                  </>
                )}

              {/* Territory Selection - for SH3 users (Dealer/Team tabs) */}
              {(tab === "Dealer" || tab === "Team") && userType === "SH3" && (
                <>
                  <Autocomplete
                    id="sh3-territory-select"
                    className="myDealer__categorySelect"
                    options={
                      !territoryOptions || territoryOptions?.length === 0
                        ? [{ SH2_Name__c: "Loading...", SH2_Code__c: "" }]
                        : getUniqueListBy(territoryOptions, "SH2_Code__c")
                    }
                    getOptionLabel={(option) =>
                      `${option?.SH2_Name__c}${
                        option?.SH2_Code__c ? `:${option?.SH2_Code__c}` : ""
                      }`
                    }
                    value={territory}
                    onChange={(e, newValue) => {
                      setTerritory(newValue);
                      onChangeExtended(e, newValue, "territory");
                    }}
                    renderInput={(params) => <TextField {...params} placeholder="Select Territory" />}
                  />
                  {!feedback.territory && formValidation?.territory && (
                    <p className="m-0 text-[#EF5464] text-[0.60rem] py-[5px] whitespace-nowrap">{formValidation?.territory}</p>
                  )}
                </>
              )}

              {/* Dealer/WSS/Product Selection */}
              {tab !== "Team" && !_.isEmpty(userType) && (
                <>
                  <Autocomplete
                    value={wssDealerOrProduct}
                    id="dealer-wss-product-select"
                    className="myDealer__categorySelect"
                    options={
                      !dealerLoader
                        ? _.isEmpty(getNameList())
                          ? []
                          : getNameList()
                        : [
                            {
                              Dealer_Name__c: "Loading...",
                              DealerCode__c: "",
                              CustomerName__c: "Loading...",
                              Customer_Code__c: "",
                              Product_Group_3_Description__c:
                                "Loading...",
                            },
                          ]
                    }
                    getOptionLabel={(option) => {
                      if (tab === "Dealer") {
                        return `${option?.Dealer_Name__c}${
                          option?.DealerCode__c
                            ? `:${option?.DealerCode__c}`
                            : ""
                        }`;
                      }
                      if (tab === "WSS") {
                        return `${option?.CustomerName__c}${
                          option?.Customer_Code__c
                            ? `:${option?.Customer_Code__c}`
                            : ""
                        }`;
                      }
                      if (tab === "Product") {
                        return `${option?.Product_Group_3_Description__c}`;
                      }
                    }}
                    onChange={(e, newValue) => {
                      setWssDealerOrProduct(newValue);
                      onChange(e, newValue, "dealerId");
                    }}
                    renderInput={(params) => <TextField {...params} placeholder={`${tab} name`} />}
                  />
                  {!feedback.dealerId && formValidation?.dealerId && (
                    <p className="m-0 text-[#EF5464] text-[0.60rem] py-[5px] whitespace-nowrap">{formValidation?.dealerId}</p>
                  )}
                </>
              )}

              {/* Title Input */}
              <Input
                type="text"
                name="Title"
                value={feedback?.Title}
                placeholder="Enter title here"
                onChange={(e) => onChangeExtended(e)}
                error={!feedback.Title && formValidation?.Title}
                errorMessage={formValidation?.Title}
              />

              {/* Observation Textarea */}
              <Textarea
                name="Observation"
                rows={8}
                placeholder="Enter your observation here"
                value={feedback?.Observation}
                onChange={(e) => onChangeExtended(e)}
                error={!feedback.Observation && formValidation?.Observation}
                errorMessage={formValidation?.Observation}
              />

              {/* Action Buttons */}
              <div className="flex items-start m-0 mt-2">
                <div className="flex-1 p-0">
                  <RWebShare
                    data={{
                      text: "Checkout below page for new feedback",
                      url: window.location.href,
                      title: "New Feedback",
                    }}
                  >
                    <p className="p-0 m-0 font-roboto font-normal text-[0.606rem] sm:text-[0.8rem] leading-[0.688rem] text-[#2cc26a] mt-2 flex items-center cursor-pointer">
                      <Share2 className="text-[#2cc26a] h-3 w-3 mr-1.5" />
                      Share
                    </p>
                  </RWebShare>
                </div>
                <div className="p-0 mr-2.5">
                  <FileUpload
                    onChange={FileHandler}
                    fileName={file?.fileName}
                    error={!!formValidation?.file}
                    errorMessage={formValidation?.file}
                  />
                </div>
                <div className="p-0">
                  <Button
                    variant="success"
                    onClick={onSubmit}
                    className="text-[0.606rem] sm:text-[0.8rem] leading-[0.8rem] sm:leading-[1.1rem] whitespace-nowrap w-[90%] py-0.5"
                  >
                    Submit
                  </Button>
                </div>
              </div>
            </form>
          </div>

          {/* Previous Observations */}
          <div className="p-2.5">
            <PreviousObservation />
          </div>
        </div>

        {/* Success Modal */}
        {isSubmitModalOpen && (
          <ModalPopup
            className="ModalPopup_Submitted"
            isModalOpen={isSubmitModalOpen}
            setIsModalOpen={setIsSubmitModalOpen}
            title="Submitted Successfully"
            data="New Observations form has been submitted successfully."
            backdrop={true}
            fade={false}
            icon={
              <div className="submittedIcons draftIcons">
                <ThumbsUp className="submittedSmall" />
                <ThumbsUp className="submittedLarge" />
                <ThumbsUp className="submittedSmall" />
              </div>
            }
          />
        )}
      </div>
    </>
  );
};

export default connect(null, {
  addFeedbackApi,
  getZSMTerritoryApi,
  getZSMDealerNameApi,
  getZSMWssNameApi,
  getDealerNameApiForHOUser,
  getRmdmWssApi,
  getDealerNameApi,
})(NewFeedback);
